import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Alert,
  SelectChangeEvent,
  IconButton,
  Tooltip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import { Refresh, FileDownload } from '@mui/icons-material';
import { FilterContext } from '../../contexts/FilterContext';
import SamDetailModal from './SamDetailModal';
import moment from 'moment';
import { formatGermanDateTime } from '../../helpers/dateFuncs';
import { formatNumber } from '../../helpers/numberUtils';
import { getApiBaseUrl } from '../../helpers/apiUtils';
import InfoTip from '../InfoTip';
import { EMPTY_SUMMARY, num, SamData, TimelineSummary } from './samTimelineTypes';

// Ein Eintrag je FT-Mailing ueber Iterable (Webinar/PLX). Summen kommen aus `summary`
// des Endpunkts (gesamte gefilterte Menge), nicht aus den geladenen Zeilen.
const SERVER_LIMIT = 500;

const COLUMNS: { key: keyof SamData; label: string; tip?: string; align?: 'right' }[] = [
  { key: 'send_time', label: 'Versand' },
  { key: 'subject', label: 'Betreff' },
  { key: 'campaign_type', label: 'Typ' },
  { key: 'recipients', label: 'Empfänger', align: 'right' },
  { key: 'open_rate', label: 'Öffnungsrate', align: 'right', tip: 'Eindeutige Öffner ÷ Empfänger' },
  { key: 'click_rate', label: 'Klickrate', align: 'right', tip: 'Eindeutige Klicker ÷ Empfänger' },
  {
    key: 'anfos',
    label: 'Anfos',
    align: 'right',
    tip: 'Bestellungen, die diesem Versand zugeordnet sind (nicht abgelehnt).',
  },
  { key: 'anfo_rate', label: 'Anfo-Rate', align: 'right', tip: 'Anfos ÷ Empfänger' },
  {
    key: 'etkp',
    label: 'ETKP',
    align: 'right',
    tip: 'CPA-Wert je 1.000 Empfänger.',
  },
  {
    key: 'cpa_revenue',
    label: 'CPA-Wert',
    align: 'right',
    tip: 'Jede Anfo mit ihrem eigenen CPA-Wert bewertet — kein Zahlungseingang.',
  },
  {
    key: 'actual_revenue',
    label: 'Umsatz (bezahlt)',
    align: 'right',
    tip: 'Tatsächlich bezahlter Umsatz der Anfos, Gutschriften abgezogen.',
  },
];

const typeChip = (type: SamData['campaign_type']) =>
  type === 'webinar_mailing' ? (
    <Chip label="WEBINAR" color="info" size="small" />
  ) : (
    <Chip label="PLX" color="error" size="small" />
  );

const fmtPct = (v: unknown) => `${formatNumber(num(v), 2)} %`;
const fmtEur = (v: unknown) => `${formatNumber(num(v), 2)} €`;

const SamTimelineTable: React.FC = () => {
  const { filterState } = useContext(FilterContext)!;
  const [data, setData] = useState<SamData[]>([]);
  const [summary, setSummary] = useState<TimelineSummary>(EMPTY_SUMMARY);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [campaignTypeFilter, setCampaignTypeFilter] = useState<string>('all');
  const [selectedSam, setSelectedSam] = useState<SamData | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [orderBy, setOrderBy] = useState<keyof SamData>('send_time');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [reloadKey, setReloadKey] = useState(0);

  const buildParams = (limit: number) =>
    new URLSearchParams({
      start_date: moment(filterState.startDate).format('YYYY-MM-DD'),
      end_date: moment(filterState.endDate).format('YYYY-MM-DD'),
      campaign_type: campaignTypeFilter,
      limit: String(limit),
      offset: '0',
    });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`/api/sam-timeline?${buildParams(SERVER_LIMIT)}`);
        if (!response.ok) throw new Error('Timeline konnte nicht geladen werden');
        const result = await response.json();
        if (cancelled) return;
        setData(result.data || []);
        setSummary({ ...EMPTY_SUMMARY, ...(result.summary || {}) });
        setPage(0);
      } catch (err: any) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [filterState.startDate, filterState.endDate, campaignTypeFilter, reloadKey]);

  const handleExport = () => {
    window.open(`${getApiBaseUrl()}/api/sam-timeline?${buildParams(10000)}`, '_blank');
  };

  const comparable = (value: unknown): number | string => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'number') return value;
    const s = String(value);
    if (s !== '' && !Number.isNaN(Number(s))) return Number(s);
    if (s.includes('T')) return new Date(s).getTime();
    return s;
  };

  const sorted = data.slice().sort((a, b) => {
    const av = comparable(a[orderBy]);
    const bv = comparable(b[orderBy]);
    const cmp = av < bv ? -1 : av > bv ? 1 : 0;
    return order === 'asc' ? cmp : -cmp;
  });
  const paginated = sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  if (loading && data.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ margin: 2 }}>
        {error}
      </Alert>
    );
  }

  const cell = (sam: SamData, key: keyof SamData) => {
    switch (key) {
      case 'send_time':
        return formatGermanDateTime(sam.send_time);
      case 'subject':
        return (
          <span title={sam.subject}>
            {sam.subject && sam.subject.length > 60
              ? `${sam.subject.substring(0, 60)}…`
              : sam.subject}
          </span>
        );
      case 'campaign_type':
        return typeChip(sam.campaign_type);
      case 'recipients':
      case 'anfos':
        return formatNumber(num(sam[key]), 0);
      case 'open_rate':
      case 'click_rate':
      case 'anfo_rate':
        return fmtPct(sam[key]);
      default:
        return fmtEur(sam[key]);
    }
  };

  const totals: Partial<Record<keyof SamData, string>> = {
    subject: `Insgesamt (${formatNumber(summary.sends, 0)} Sends)`,
    recipients: formatNumber(summary.recipients, 0),
    open_rate: fmtPct(summary.open_rate),
    click_rate: fmtPct(summary.click_rate),
    anfos: formatNumber(summary.anfos, 0),
    anfo_rate: fmtPct(summary.anfo_rate),
    etkp: fmtEur(summary.etkp),
    cpa_revenue: fmtEur(summary.cpa_revenue),
    actual_revenue: fmtEur(summary.actual_revenue),
  };

  return (
    <Box>
      <Box display="flex" gap={2} mb={2} alignItems="center" flexWrap="wrap">
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Mailing-Typ</InputLabel>
          <Select
            value={campaignTypeFilter}
            label="Mailing-Typ"
            onChange={(e: SelectChangeEvent) => setCampaignTypeFilter(e.target.value)}
          >
            <MenuItem value="all">Alle</MenuItem>
            <MenuItem value="webinar_mailing">Webinar-Mailings</MenuItem>
            <MenuItem value="plx_mailing">PLX-Mailings</MenuItem>
          </Select>
        </FormControl>
        <Box flexGrow={1} />
        <Tooltip title="Neu laden">
          <IconButton onClick={() => setReloadKey((k) => k + 1)} disabled={loading}>
            <Refresh />
          </IconButton>
        </Tooltip>
        <Tooltip title="Als JSON öffnen (bis 10.000 Sends)">
          <IconButton onClick={handleExport}>
            <FileDownload />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Summen ueber die gesamte gefilterte Menge (Endpunkt-summary), nicht nur die Seite */}
      <Box mb={2} display="flex" gap={1.5} flexWrap="wrap">
        <Chip label={`Sends: ${formatNumber(summary.sends, 0)}`} variant="outlined" />
        <Chip label={`Empfänger: ${formatNumber(summary.recipients, 0)}`} variant="outlined" />
        <Chip label={`Anfos: ${formatNumber(summary.anfos, 0)}`} variant="outlined" />
        <Chip label={`CPA-Wert: ${fmtEur(summary.cpa_revenue)}`} variant="outlined" />
        <Chip label={`Umsatz (bezahlt): ${fmtEur(summary.actual_revenue)}`} variant="outlined" />
        <Chip label={`ETKP: ${fmtEur(summary.etkp)}`} variant="outlined" />
      </Box>

      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 900 }} size="small">
          <TableHead>
            <TableRow>
              {COLUMNS.map((col) => (
                <TableCell key={col.key} align={col.align}>
                  <TableSortLabel
                    active={orderBy === col.key}
                    direction={orderBy === col.key ? order : 'asc'}
                    onClick={() => {
                      setOrder(orderBy === col.key && order === 'asc' ? 'desc' : 'asc');
                      setOrderBy(col.key);
                    }}
                  >
                    {col.label}
                  </TableSortLabel>
                  {col.tip ? <InfoTip text={col.tip} /> : null}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginated.map((sam) => (
              <TableRow
                key={sam.campaign_id}
                sx={{
                  '&:nth-of-type(odd)': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.08)' },
                }}
                onClick={() => {
                  setSelectedSam(sam);
                  setModalOpen(true);
                }}
              >
                {COLUMNS.map((col) => (
                  <TableCell key={col.key} align={col.align}>
                    {cell(sam, col.key)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            <TableRow sx={{ backgroundColor: 'rgba(0, 0, 0, 0.08)' }}>
              {COLUMNS.map((col) => (
                <TableCell key={col.key} align={col.align} sx={{ fontWeight: 'bold' }}>
                  {totals[col.key] ?? '—'}
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={data.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(_e, p) => setPage(p)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />

      <SamDetailModal open={modalOpen} onClose={() => setModalOpen(false)} sam={selectedSam} />
    </Box>
  );
};

export default SamTimelineTable;
